import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { GrowthBook, GrowthBookProvider } from "@growthbook/growthbook-react";

import {
  SENTRY_DSN,
  IS_SENTRY_AVAILABLE,
  SENTRY_TRACES_SAMPLE_RATE,
  IS_GROWTHBOOK_ENABLED,
  IS_DEV_ENV,
  LOCALSTORAGE_ITEMS,
  NOTIFICATION_GROUPS,
  NOTIFICATION_POSITIONS,
  PRESELECTED_VOICE_PARAM,
} from "./constants";
import "./index.css";
import App from "./App";
import store from "./store";
import { initAction } from "actions/initialization";
import { notificationDisplay } from "actions/notification";
import ConditionalWrapper from "components/common/ConditionalWrapper";
import localStorageService from "services/localStorage";
import { arrayContains, getCsrfToken } from "utils";
import eventsService, { EVENTS } from "services/events";

if (IS_SENTRY_AVAILABLE) {
  Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [new Sentry.BrowserTracing()],
    // Performance Monitoring
    tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE || 1.0, // Capture 100% of the transactions, reduce in production!
    environment: process.env.REACT_APP_APP_ENV,
    ignoreErrors: [/Failed to fetch/],
  });
}
let growthbook;
if (IS_GROWTHBOOK_ENABLED) {
  growthbook = new GrowthBook({
    apiHost: "https://cdn.growthbook.io",
    clientKey: process.env.REACT_APP_GROWTHBOOK_ID,
    enableDevMode: IS_DEV_ENV,
    trackingCallback: (experiment, result) => {
      eventsService.track(EVENTS.GROWTHBOOK, {
        experimentId: experiment.key,
        variationValue: result.value,
      });
    },
  });
}

store.dispatch(initAction());

const availablePromos = process.env.REACT_APP_PROMO_CODES_PARAMS.split(",");
const searchParams = new URLSearchParams(document.location.search);
const keys = Array.from(searchParams.keys());
const promoKeys = arrayContains(availablePromos, keys);
if (promoKeys[0]) {
  localStorageService.setItem(LOCALSTORAGE_ITEMS.promo, {
    code: searchParams.get(promoKeys[0]),
    place: promoKeys[0],
  });
  if (!getCsrfToken()) {
    store.dispatch(
      notificationDisplay(
        "success",
        "Your promo code has been recognized and will be applied upon successful sign-in or registration.",
        NOTIFICATION_GROUPS.general,
        [NOTIFICATION_POSITIONS.center, NOTIFICATION_POSITIONS.bottom]
      )
    );
  }
}

const preselectedVoiceId = searchParams.get(PRESELECTED_VOICE_PARAM);
if (preselectedVoiceId) {
  localStorageService.setItem(
    LOCALSTORAGE_ITEMS.preselectedVoiceId,
    preselectedVoiceId
  );
}

const setVh = () => {
  document.documentElement.style.setProperty(
    "--vh",
    `${window.innerHeight * 0.01}px`
  );
};

window.addEventListener("resize", () => {
  setVh();
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <Provider store={store}>
    {/* <Sentry> */}
    <BrowserRouter>
      <ConditionalWrapper
        condition={!!growthbook}
        wrapper={(children) => (
          <GrowthBookProvider growthbook={growthbook}>
            {children}
          </GrowthBookProvider>
        )}
      >
        <App />
      </ConditionalWrapper>
    </BrowserRouter>
    {/* </Sentry> */}
  </Provider>
);
